<script>
/**
 * @component PatientAppointmentView
 * @description Vista de atención de paciente con header compacto,
 *              tabs Atención/Historia y sidebar de resumen.
 * @example <PatientAppointmentView />
 */
import Spinner from "@/components/ui/Spinner";
import { mapGetters } from "vuex";
import FormBox from "@/components/patient/dashboard/FormBox";
import IndicationForm from "@/components/appointment/indications/IndicationForm";
import NotesBox from "@/components/patient/dashboard/NotesBox";
import { isIndicationsEnabled } from "@/services/config";
import helperService from "@/services/helper";
import VideoRoom from "@/components/video-room/VideoRoom";
import { get } from "lodash";
import appointmentService from "@/services/appointment";
import indicationService from "@/services/indication";
import { filterPersistibleIndications } from "@/services/indication-filter";
import formService from "@/services/form";
import programPatientService from "@/services/programPatient";
import { INDICATIONS_TYPES } from "@/services/constants";
import fileUploadConfigService from "@/services/fileupload-config";
import api from "@/services/api";
import { cloneDeep } from "lodash";
import SpinnerModal from "@/components/ui/SpinnerModal";
import AppointmentSummary from "@/components/appointment/AppointmentSummary";
import PatientHistory from "./PatientHistory.vue";
import AIConsultationDrawer from "@/components/ai/AIConsultationDrawer.vue";
import StudyResultModal from "@/components/patient/StudyResultModal.vue";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";

export default {
  name: "PatientAppointmentView",
  components: {
    Spinner,
    SpinnerModal,
    FormBox,
    IndicationForm,
    NotesBox,
    VideoRoom,
    AppointmentSummary,
    PatientHistory,
    AIConsultationDrawer,
    StudyResultModal,
  },

  data() {
    return {
      patient: undefined,
      appointment: "",
      videoRoomToken: "",
      isLoadingPatientData: true,
      isLoading: false,
      isSavingIndications: false,
      roomInfo: {},
      isIndicationsEnabled,
      appointmentStatus: "appointment",
      // Se agrega filter para no mostrar imagesResults y labResults entre las opciones de creación
      forms: cloneDeep(
        INDICATIONS_TYPES.filter(
          (form) => form.type !== "imagesResults" && form.type !== "labResults"
        ).sort((a, b) => {
          const order = [
            "evolution",
            "medications",
            "lab",
            "images",
            "vitals",
            "practices",
            "notes",
            "certificates",
            "forms",
            "program",
          ];
          const ia = order.indexOf(a.type);
          const ib = order.indexOf(b.type);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        })
      ),
      savedIndications: [],
      savedFiles: {},
      completedForms: [],
      patientIndicationsFilter: [
        "medications",
        "images",
        "lab",
        "practices",
        "notes",
        "certificates",
      ],
      otherIndicationsFilter: ["evolution"],
      // IA
      aiDrawerVisible: false,
      aiTranscription: "",
      isRecording: false,
      speechRecognition: null,
      studyResultModal: {
        visible: false,
        type: "lab",
        sourceIndication: null,
      },
      /** @description Historial reciente del paciente para el sidebar */
      recentHistory: [],
      /** @description Modal con los documentos que el paciente adjuntó a la consulta */
      attachmentsModal: {
        visible: false,
        loading: false,
        items: [],
      },
    };
  },

  computed: {
    ...mapGetters([]),

    get() {
      return get;
    },

    /** @returns {boolean} Si la atención es virtual */
    isVirtualAppointment() {
      return this.appointment?.attentionType === "virtual";
    },

    canSaveIndications() {
      const hasIndications =
        this.savedIndications.length > 0 &&
        this.savedIndications.some(
          (indication) =>
            !indication.createdAt &&
            this.patientIndicationsFilter.includes(indication.type)
        );

      const hasCompletedForms = this.completedForms.length > 0;

      const hasCompletedPrograms = this.hasCompletedPrograms();

      return (
        !this.isSavingIndications &&
        (hasIndications || hasCompletedForms || hasCompletedPrograms)
      );
    },

    getAllCompletedForms() {
      return this.completedForms;
    },
  },

  created() {
    this.fetchAppointmentData();

    this.$bus.$on("updateIndication", (indication, isDeleted = false) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indication.key
      );
      if (indicationIndex !== -1) {
        if (!isDeleted) {
          this.$set(this.savedIndications, indicationIndex, indication);
        } else {
          this.savedIndications.splice(indicationIndex, 1);
        }
      } else {
        if (!isDeleted) {
          this.savedIndications.push(indication);
        }
      }
    });

    this.$bus.$on("filesUploaded", (indicationKey) => {
      const indicationIndex = this.savedIndications.findIndex(
        (e) => e.key === indicationKey
      );
      if (indicationIndex !== -1) {
        this.$set(this.savedIndications, indicationIndex, {
          ...this.savedIndications[indicationIndex],
          hasFiles: false,
        });
      }

      if (this.savedIndications.every((indication) => !indication.hasFiles)) {
        this.isSavingIndications = false;
      }
    });

    this.$bus.$on("form-completed", this.updateCompletedForms);
    this.$bus.$on("form-updated", this.updateCompletedForms);
    this.$bus.$on("program-form-completed", () => {
      this.$forceUpdate();
    });
  },

  beforeDestroy() {
    this.$bus.$off("form-completed", this.updateCompletedForms);
    this.$bus.$off("form-updated", this.updateCompletedForms);
    this.$bus.$off("program-form-completed");
  },

  methods: {
    /** @returns {string} Iniciales del paciente para avatar */
    getInitials(patient) {
      if (!patient) return "?";
      const first = (patient.firstName || "")[0] || "";
      const last = (patient.lastName || "")[0] || "";
      return (first + last).toUpperCase();
    },

    updateCompletedForms() {
      const indicationFormRef = this.$refs.indicationForm;
      if (!indicationFormRef) return;

      const allForms = [];
      const allIndicationRefs = indicationFormRef.$refs;

      for (const refName of Object.keys(allIndicationRefs)) {
        if (refName.startsWith("indications-")) {
          const indicationComponent = allIndicationRefs[refName];
          if (indicationComponent?.[0]?.completedForms) {
            const completedForms = indicationComponent[0].completedForms;
            for (const indicationKey of Object.keys(completedForms)) {
              allForms.push(...completedForms[indicationKey]);
            }
          }
        }
      }
      this.completedForms = allForms;
    },

    hasCompletedPrograms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return false;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    return true;
                  }
                }
              }
            }
          }
        }

        return false;
      } catch (error) {
        console.error("Error al verificar programas completados:", error);
        return false;
      }
    },
    triggerUpload(createdIndication) {
      this.$refs.indicationForm.triggerUpload(createdIndication);
    },

    async fetchAppointmentData() {
      try {
        const appointment = await appointmentService.getAppointmentById({
          id: this.$route.params.appointmentId,
          params: { populate: ["institution", "medicalSpecialty", "patient"] },
        });

        if (appointment) {
          this.appointment = appointment;
          this.patient = appointment.patient;

          const updated = await appointmentService.startAppointment(
            appointment._id
          );
          if (updated) {
            this.appointment = updated;
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    goBack() {
      this.$router.back();
    },

    goToPatientDashboardTab() {
      let routeData = this.$router.resolve({
        name: "patient-dashboard",
        params: { id: this.patient._id },
      });
      window.open(routeData.href, "_blank");
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },

    getGender(user) {
      switch (user.gender) {
        case "m":
          return "Hombre";

        case "f":
          return "Mujer";

        default:
          return "Otro";
      }
    },

    setAppointmentStatus(status) {
      this.appointmentStatus = status;
    },

    isHighlighted(value) {
      if (value === this.appointmentStatus) {
        return "selected";
      } else {
        return "not-selected";
      }
    },

    getMedicalInsuranceNumber,
    getMedicalInsurancePlan,
    getMedicalInsuranceName,

    async saveIndications(typeFilter = null) {
      try {
        const validIndications = filterPersistibleIndications(
          this.savedIndications,
          typeFilter
        );
        await indicationService.saveIndications(
          validIndications,
          this.patient._id,
          this.appointment._id,
          typeFilter,
          this.$store,
          {
            isSavingIndications: this.isSavingIndications,
            onTriggerUpload: this.triggerUpload.bind(this),
            onUpdateIndication: (updatedIndication) => {
              const index = this.savedIndications.findIndex(
                (ind) => ind.key === updatedIndication.key
              );
              if (index !== -1) {
                this.$set(this.savedIndications, index, updatedIndication);
              }
            },
            onSetSavingStatus: (status) => {
              this.isSavingIndications = status;
            },
          }
        );

        await this.sendCompletedForms();
        this.$message.success("Prescripciones enviadas con éxito");
      } catch (error) {
        console.log(error);
      }
    },

    async endAppointment() {
      try {
        await this.saveIndications();
        await appointmentService.endAppointment(this.appointment._id);
        this.$message.success("Atención finalizada con éxito");

        this.$router.push({
          name: "appointment-schedule",
        });
      } catch (error) {
        console.log(error);
      }
    },

    async sendCompletedForms() {
      try {
        const indicationFormRef = this.$refs.indicationForm;
        if (!indicationFormRef) return;

        const allIndicationRefs = indicationFormRef.$refs;

        for (const refName of Object.keys(allIndicationRefs)) {
          if (refName.startsWith("indications-")) {
            const indicationComponent = allIndicationRefs[refName];

            if (indicationComponent?.[0]?.completedForms) {
              const completedForms = indicationComponent[0].completedForms;

              for (const indicationKey of Object.keys(completedForms)) {
                const formsToSend = completedForms[indicationKey];

                for (const completedForm of formsToSend) {
                  if (!completedForm.isSent) {
                    await this.sendFormToBackend(
                      completedForm,
                      indicationKey,
                      indicationComponent[0]
                    );
                  }
                }
              }
            }

            if (indicationComponent?.[0]?.completedProgramForms) {
              const completedProgramForms =
                indicationComponent[0].completedProgramForms;

              for (const indicationKey of Object.keys(completedProgramForms)) {
                const programForms = completedProgramForms[indicationKey];

                for (const programId of Object.keys(programForms)) {
                  const formsToSend = programForms[programId];

                  if (formsToSend && formsToSend.length > 0) {
                    await this.sendProgramToBackend(programId, formsToSend);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error al enviar formularios completados:", error);
      }
    },

    async sendFormToBackend(completedForm, indicationKey, indicationComponent) {
      try {
        const formData = {
          form: completedForm.form?._id || completedForm._id,
          patient: this.patient._id,
          appointment: this.appointment._id,
          sections: completedForm.sections || [],
          answeredAt: completedForm.answeredAt || new Date(),
          status: completedForm.status || "open",
          createdBy: this.$store.state.user,
        };

        const createdForm = await formService.postPatientForm(formData);

        completedForm.isSent = true;
        completedForm._id = createdForm._id;
        completedForm.sentAt = new Date();

        if (completedForm.hasFiles && completedForm.dropzoneFiles?.length > 0) {
          await this.uploadFormFiles(
            createdForm._id,
            completedForm.dropzoneFiles
          );
        }

        this.updateIndication(indicationComponent);
      } catch (error) {
        console.error("Error al enviar formulario al backend:", error);
      }
    },

    async uploadFormFiles(formId, files) {
      try {
        const formData = new FormData();

        files.forEach((file) => {
          const nativeFile = file.file || file;
          if (nativeFile instanceof File || nativeFile instanceof Blob) {
            formData.append("files", nativeFile, nativeFile.name);
          }
        });

        const uploadUrl = fileUploadConfigService.getFormFilesEndpoint(formId);
        await api.put(uploadUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$message.success("Archivos adjuntos subidos exitosamente");
      } catch (error) {
        console.error("Error al subir archivos del formulario:", error);
        this.$message.error(
          `Error al subir archivos: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    },

    async sendProgramToBackend(programId, completedForms) {
      try {
        const completedFormsData = completedForms.map((completedForm) => ({
          name:
            completedForm.name ||
            completedForm.form?.name ||
            "Formulario del Programa",
          description:
            completedForm.description ||
            completedForm.form?.description ||
            "Formulario completado del programa",
          sections: completedForm.sections || [],
          status: "closed",
          answeredAt:
            completedForm.answeredAt ||
            completedForm.createdAt ||
            new Date().toISOString(),
        }));

        const programData = {
          program: programId,
          patient: this.patient._id,
          appointment: this.appointment._id,
          status: "pending",
          completedForms: completedFormsData,
        };

        const result = await programPatientService.assignProgramToPatient(
          programData
        );

        if (result.initialData && Array.isArray(result.initialData)) {
          for (let i = 0; i < completedForms.length; i++) {
            const completedForm = completedForms[i];
            const createdForm = result.initialData[i];

            if (
              completedForm.hasFiles &&
              completedForm.dropzoneFiles?.length > 0 &&
              createdForm?._id
            ) {
              await this.uploadFormFiles(
                createdForm._id,
                completedForm.dropzoneFiles
              );
            }
          }
        }

        completedForms.forEach((completedForm) => {
          completedForm.isSent = true;
          completedForm.sentAt = new Date();
        });
      } catch (error) {
        console.error("Error al enviar programa al backend:", error);
      }
    },

    /**
     * @description Inicia o detiene la grabación con Web Speech API
     */
    toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },

    /**
     * @description Inicia la grabación con SpeechRecognition del browser
     */
    startRecording() {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        this.$message.error(
          "Tu navegador no soporta grabación de voz. Usá Chrome o Edge."
        );
        return;
      }
      this.aiTranscription = "";
      this.isRecording = true;

      const startSession = () => {
        if (!this.isRecording) return;

        const recognition = new SpeechRecognition();
        recognition.lang = "es-AR";
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          console.log("Transcripto:", transcript);
          this.aiTranscription += transcript + " ";
        };

        recognition.onerror = (event) => {
          console.log("Error speech:", event.error);
          if (event.error === "no-speech" && this.isRecording) {
            startSession();
          }
        };

        recognition.onend = () => {
          if (this.isRecording) {
            startSession();
          }
        };

        this.speechRecognition = recognition;
        recognition.start();
      };

      startSession();
    },

    /**
     * @description Detiene la grabación y abre el drawer de IA
     */
    stopRecording() {
      this.isRecording = false;
      if (this.speechRecognition) {
        this.speechRecognition.onend = () => {
          // Esperar que termine la última sesión antes de abrir el drawer
          if (this.aiTranscription.trim()) {
            this.aiDrawerVisible = true;
          } else {
            this.$message.warning("No se detectó texto en la grabación");
          }
        };
        this.speechRecognition.stop();
        this.speechRecognition = null;
      } else {
        if (this.aiTranscription.trim()) {
          this.aiDrawerVisible = true;
        } else {
          this.$message.warning("No se detectó texto en la grabación");
        }
      }
    },

    /**
     * @description Aplica resumen/síntomas como texto de evolución y pasa el diagnóstico
     *              al selector CIE-10 para que busque y auto-seleccione el código correspondiente.
     * @param {Object} data - { summary, symptoms, diagnosis }
     */
    onApplySummary(data) {
      const lines = [];
      if (data.summary) lines.push(data.summary);
      if (data.symptoms && data.symptoms.length)
        lines.push("Síntomas: " + data.symptoms.join(", "));
      const text = lines.join("\n");
      this.$bus.$emit("ai-apply-evolution", {
        description: text,
        diagnosisTerm: data.diagnosis || null,
      });
    },

    /**
     * @description Emite sugerencias de vitales al formulario de indicaciones
     * @param {Array} vitals
     */
    onApplyVitals(vitals) {
      this.$bus.$emit("ai-apply-vitals", vitals);
    },

    /**
     * @description Emite sugerencias de imagen al formulario de indicaciones
     * @param {Array} imaging
     */
    onApplyImaging(imaging) {
      this.$bus.$emit("ai-apply-imaging", imaging);
    },

    /**
     * @description Emite sugerencias de laboratorio al formulario de indicaciones
     * @param {Array} lab
     */
    onApplyLab(lab) {
      this.$bus.$emit("ai-apply-lab", lab);
    },

    /**
     * @description Emite sugerencias de medicamentos al formulario de indicaciones
     * @param {Array} medications
     */
    onApplyMedications(medications) {
      this.$bus.$emit("ai-apply-medications", medications);
    },

    /**
     * @description Abre el modal con los documentos que el paciente adjuntó a
     * la consulta y los trae con URLs firmadas para abrirlos.
     */
    async openAttachmentsModal() {
      this.attachmentsModal.visible = true;
      this.attachmentsModal.loading = true;
      try {
        const { attachments } =
          await appointmentService.getAppointmentAttachments(
            this.$route.params.appointmentId
          );
        this.attachmentsModal.items = attachments || [];
      } catch {
        this.attachmentsModal.items = [];
        if (this.$message) {
          this.$message.error("No se pudieron cargar los adjuntos.");
        }
      }
      this.attachmentsModal.loading = false;
    },

    /**
     * @description Abre el modal de carga de resultado desde la atención activa.
     * @param {Object} payload - { type, indicationId }
     */
    onUploadResult({ type, indicationId }) {
      this.studyResultModal = {
        visible: true,
        type,
        sourceIndication: indicationId ? { _id: indicationId } : null,
      };
    },

    updateIndication(indication) {
      this.$bus.$emit("updateIndication", cloneDeep(indication), false);
    },
  },
};
</script>

<template lang="pug">
  div
    section(v-if="patient")
      //- Header compacto estilo Quantux
      .patient-card
        .patient-header__top
          .patient-header__left
            .patient-avatar
              span {{ getInitials(patient) }}
            .patient-header__info
              span.patient-header__label Atendiendo a
              .patient-header__name-row
                h1.patient-header__name(@click="goToPatientDashboardTab()") {{ getFullName(patient) }}
                span.attention-type-tag(
                  :class="isVirtualAppointment ? 'attention-type-tag--virtual' : 'attention-type-tag--presencial'"
                ) {{ isVirtualAppointment ? 'Virtual' : 'Presencial' }}
          .patient-header__actions
            button.btn-action.btn-ai(
              :class="{ 'btn-ai--recording': isRecording }"
              @click="toggleRecording"
              :title="isRecording ? 'Detener y analizar con IA' : 'Grabar consulta con IA'"
            )
              i(:class="isRecording ? 'el-icon-video-pause' : 'el-icon-microphone'")
              span {{ isRecording ? 'Detener' : 'Grabar con IA' }}
            button.btn-action.btn-action--secondary(@click="openAttachmentsModal") Ver adjuntos
            button.btn-action.btn-action--primary(
              @click="saveIndications(patientIndicationsFilter)"
              :disabled="!canSaveIndications || isSavingIndications"
            ) Enviar prescripciones
            button.btn-action.btn-action--danger(
              :disabled="isSavingIndications"
              @click="endAppointment"
            ) {{ isSavingIndications ? 'Finalizando...' : 'Finalizar atención' }}
        .patient-header__details
          .detail-item
            i.el-icon-date
            span {{ patient.bornAt | age }}
          span.detail-separator &middot;
          .detail-item
            i.el-icon-postcard
            span {{ get(patient, 'governmentId.type', 'DNI').toUpperCase() }} {{ get(patient, 'governmentId.number', 'N/A') }}
          span.detail-separator &middot;
          .detail-item
            i.el-icon-phone
            span {{ patient.phone || 'N/A' }}
          template(v-if="getMedicalInsuranceName(patient)")
            span.detail-separator &middot;
            .detail-item
              i.el-icon-first-aid-kit
              span {{ getMedicalInsuranceName(patient) }}
            template(v-if="getMedicalInsurancePlan(patient)")
              span.detail-separator &middot;
              .detail-item
                span {{ getMedicalInsurancePlan(patient) }}
            template(v-if="getMedicalInsuranceNumber(patient)")
              span.detail-separator &middot;
              .detail-item
                span \#{{ getMedicalInsuranceNumber(patient) }}
          template(v-if="patient.email")
            span.detail-separator &middot;
            .detail-item
              i.el-icon-message
              span {{ patient.email }}

      //- Layout principal
      .appointment-layout
        .appointment-left
          .appointment-tabs
            .appointment-tab(
              :class="{ 'appointment-tab--active': appointmentStatus === 'appointment' }"
              @click="setAppointmentStatus('appointment')"
            )
              i.el-icon-edit-outline
              span Atención
            .appointment-tab(
              :class="{ 'appointment-tab--active': appointmentStatus === 'history' }"
              @click="setAppointmentStatus('history')"
            )
              i.el-icon-time
              span Historia clínica

          indication-form(
            v-show="isIndicationsEnabled && appointmentStatus === 'appointment'"
            ref="indicationForm"
            :forms="forms"
            :savedIndications="savedIndications"
            :appointment="appointment"
          )
          patient-history(
            v-show="appointmentStatus === 'history'"
            :patientId="patient._id"
            )

        .appointment-right
          //- Video
          video-room(
            v-if="appointment.attentionType === 'virtual'"
            :patientId="patient._id"
            :appointmentId="appointment.id"
            :autoJoin="true"
          )
          //- Resumen
          AppointmentSummary(
            :savedIndications="savedIndications"
            context="appointment"
            :completedForms="getAllCompletedForms"
            @upload-result="onUploadResult"
          )
          //- Subir resultados
          .appointment-study-actions
            button.btn-study-upload(@click="onUploadResult({ type: 'lab' })") + Resultado de laboratorio
            button.btn-study-upload(@click="onUploadResult({ type: 'images' })") + Resultado de imagen
          //- Historial reciente
          .appointment-recent(v-if="recentHistory && recentHistory.length")
            .appointment-recent__title Historial reciente
            .appointment-recent__list
              .appointment-recent__item(v-for="item in recentHistory" :key="item._id")
                span.appointment-recent__date {{ item.startsAt | formatDate }}
                span.appointment-recent__text {{ item.summary }}

    study-result-modal(
      v-if="studyResultModal.visible"
      v-model="studyResultModal.visible"
      :patientId="patient ? patient._id : ''"
      :type="studyResultModal.type"
      :sourceIndication="studyResultModal.sourceIndication"
      :appointmentId="appointment ? appointment._id : ''"
    )

    AIConsultationDrawer(
      :visible="aiDrawerVisible"
      :transcription="aiTranscription"
      :patientId="patient ? patient._id : ''"
      :appointmentId="appointment ? appointment._id : ''"
      @close="aiDrawerVisible = false"
      @apply-summary="onApplySummary"
      @apply-vitals="onApplyVitals"
      @apply-imaging="onApplyImaging"
      @apply-lab="onApplyLab"
      @apply-medications="onApplyMedications"
    )

    el-dialog(
      title="Adjuntos de la consulta"
      :visible.sync="attachmentsModal.visible"
      width="460px"
      append-to-body
    )
      .qx-attachments
        p.qx-attachments__desc Documentos que el paciente subió para esta consulta.
        .qx-attachments__state(v-if="attachmentsModal.loading") Cargando adjuntos...
        .qx-attachments__state(v-else-if="!attachmentsModal.items.length")
          | El paciente no adjuntó documentos para esta consulta.
        .qx-attachments__list(v-else)
          a.qx-attachment(
            v-for="a in attachmentsModal.items"
            :key="a._id"
            :href="a.url"
            target="_blank"
            rel="noopener"
          )
            i.el-icon-document.qx-attachment__ic
            span.qx-attachment__name {{ a.name }}
            i.el-icon-download.qx-attachment__dl
</template>

<style lang="scss" scoped>
/* ============================================
   PATIENT APPOINTMENT VIEW — Design System v2
   Navy: #002D5E | Teal: #0d9d9d | BG: #f8fafc
   ============================================ */

.patient-card {
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 24px;
  margin-bottom: 0;
  border-radius: 0;
  box-shadow: none;
}

.patient-header__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
}

.patient-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.patient-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #002d5e;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    color: white;
    font-size: 13px;
    font-weight: 700;
  }
}

.patient-header__label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: block;
}

.patient-header__name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 1px;
}

.patient-header__name {
  font-size: 18px;
  font-weight: 700;
  color: #002d5e;
  cursor: pointer;
  line-height: 1.1;
  margin: 0;

  &:hover {
    text-decoration: underline;
  }
}

.attention-type-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 9px;
  border-radius: 999px;

  &--virtual {
    border: 1.5px solid #0d9d9d;
    color: #0d9d9d;
  }

  &--presencial {
    border: 1.5px solid #3b6ea5;
    color: #3b6ea5;
  }
}

.patient-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.patient-header__details {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;

  i {
    font-size: 12px;
    color: #94a3b8;
  }
}

.detail-separator {
  color: #cbd5e1;
  font-size: 11px;
}

/* ---------- Action Buttons ---------- */
.btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--primary {
    background: #002d5e;
    color: #fff;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  }

  &--secondary {
    background: #fff;
    color: #002d5e;
    border: 1px solid #e2e8f0;

    &:hover:not(:disabled) {
      background: #f0f2f7;
    }
  }

  &--danger {
    background: #fee2e2;
    color: #dc2626;
    border: 1px solid #fca5a5;

    &:hover:not(:disabled) {
      background: #fecaca;
    }
  }
}

.btn-ai {
  background: #f0fafa;
  color: #0e7490;
  border: 1px solid #2abfbf;

  &:hover:not(:disabled) {
    background: #e6f9f9;
  }

  &--recording {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
    animation: pulse-recording 1.5s infinite;

    &:hover:not(:disabled) {
      background: #fecaca;
    }
  }
}

@keyframes pulse-recording {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* ---------- Layout ---------- */
.appointment-layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 14px;
  align-items: start;
}

.appointment-left {
  flex: 1;
  min-width: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
}

.appointment-right {
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

/* ---------- Tabs ---------- */
.appointment-tabs {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  padding: 0;
  background: transparent;
  margin-bottom: 16px;
}

.appointment-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #94a3b8;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.15s;
  background: transparent;

  &:hover {
    color: #475569;
  }

  &--active {
    color: #0d9d9d;
    border-bottom-color: #0d9d9d;
  }
}

/* ---------- Tab Body ---------- */
.appointment-tab-body {
  padding: 0;
}

/* ---------- Study Actions ---------- */
.appointment-study-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.btn-study-upload {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #b5d4f4;
  background: #e1eaf6;
  color: #0a3d7a;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: #c8d8f0;
  }
}

/* ---------- Recent History ---------- */
.appointment-recent {
  background: white;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;

  &__title {
    padding: 10px 14px;
    font-size: 12px;
    font-weight: 700;
    color: #002d5e;
    border-bottom: 0.5px solid #f1f5f9;
  }

  &__list {
    padding: 8px 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__item {
    display: flex;
    gap: 8px;
    font-size: 12px;
    padding: 4px 0;
    border-bottom: 0.5px solid #f1f5f9;

    &:last-child {
      border-bottom: none;
    }
  }

  &__date {
    color: #94a3b8;
    min-width: 36px;
    flex-shrink: 0;
  }

  &__text {
    color: #334155;
    line-height: 1.4;
  }
}

/* ---------- Responsive ---------- */
@media (max-width: 1024px) {
  .appointment-layout {
    flex-direction: column;
  }

  .appointment-right {
    width: 100%;
    position: static;
  }

  .patient-header__top {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .patient-header__actions {
    width: 100%;
    flex-wrap: wrap;
  }
}

.qx-attachments__desc {
  font-size: 13px;
  color: #6e7c90;
  margin: 0 0 14px;
}
.qx-attachments__state {
  font-size: 14px;
  color: #6e7c90;
  text-align: center;
  padding: 20px 0;
}
.qx-attachments__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.qx-attachment {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  border: 1px solid #e7ecf2;
  border-radius: 10px;
  text-decoration: none;
  color: #16243b;
}
.qx-attachment:hover {
  background: #f4f7fb;
}
.qx-attachment__ic {
  color: #05b48f;
  font-size: 17px;
}
.qx-attachment__name {
  font-size: 13.5px;
  font-weight: 600;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.qx-attachment__dl {
  color: #6e7c90;
  font-size: 16px;
}
</style>
